import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { NO_DATA } from "../../lib/format";
import { number } from "../../lib/home";

// Building blocks shared by the Home tabs (design: components/home/shared.tsx).

export function Panel({ children, className = "", ...props }) {
  return <section {...props} className={`min-w-0 rounded-lg bg-paper p-5 shadow-soft ${className}`}>{children}</section>;
}

export function SectionHeading({ title, to, action }) {
  return <div className="mb-4 flex min-w-0 items-center justify-between gap-3"><h2 className="text-h3 font-semibold text-ink">{title}</h2>{to && action && <Link to={to} className="inline-flex shrink-0 items-center gap-1 text-caption font-medium text-sky-deep hover:underline">{action}<ArrowRight size={14} strokeWidth={1.75} /></Link>}</div>;
}

/** Counts up to `value`; shows "—" while there is no value. */
export function CountUp({ value, format = number }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (value === null || value === undefined) return undefined;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || value === 0) { setShown(value); return undefined; }
    let frame = 0;
    const start = performance.now();
    const tick = (time) => {
      const progress = Math.min(1, (time - start) / 650);
      setShown(Math.round(value * (1 - Math.pow(1 - progress, 3))));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);
  if (value === null || value === undefined) return NO_DATA;
  return <>{format(shown)}</>;
}

export function KpiStrip({ items }) {
  return <Panel className="p-0"><div className={`grid min-w-0 grid-cols-2 divide-x divide-y divide-line sm:grid-cols-3 lg:divide-y-0 ${items.length > 4 ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}>{items.map((item) => {
    const max = Math.max(1, ...(item.values ?? []));
    const points = (item.values ?? []).map((value, index, all) => `${index * 100 / Math.max(1, all.length - 1)},${26 - value / max * 22}`).join(" ");
    return <div key={item.label} className="min-w-0 p-4"><p className="truncate text-caption text-muted">{item.label}</p><p className="mt-1 truncate text-h2 font-semibold tabular-nums text-ink">{item.value}</p><div className="mt-2 flex min-h-7 items-end justify-between gap-2"><p className="min-w-0 truncate text-caption text-muted">{item.delta ?? " "}</p>{(item.values?.length ?? 0) > 1 && <svg viewBox="0 0 100 28" className="h-7 w-24 shrink-0" aria-hidden="true"><polyline points={points} fill="none" stroke="var(--color-sky-deep)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}</div></div>;
  })}</div></Panel>;
}

export function StatusBadge({ children, tone = "neutral" }) {
  const toneClass = tone === "success" ? "bg-success-soft text-success" : tone === "warning" ? "bg-warning-soft text-warning" : tone === "danger" ? "bg-danger-soft text-danger" : tone === "booked" ? "bg-sky-soft text-ink" : tone === "completed" ? "bg-mist text-ink" : "bg-mist text-muted";
  return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-caption font-medium ${toneClass}`}>{children}</span>;
}

export function TableFrame({ children }) {
  return <div className="overflow-x-auto"><table className="w-full min-w-[520px] text-left text-small">{children}</table></div>;
}

export function MiniBar({ value, max, label, tone = "bg-sky-deep" }) {
  return <div className="min-w-0"><div className="mb-1 flex items-center justify-between gap-2 text-caption"><span className="truncate text-ink">{label}</span><span className="shrink-0 tabular-nums text-muted">{number(value)}</span></div><div className="h-2 overflow-hidden rounded-full bg-mist"><div className={`h-full rounded-full ${tone}`} style={{ width: `${max && value ? Math.min(100, value / max * 100) : 0}%` }} /></div></div>;
}

export function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return <div className="rounded-sm bg-paper p-3 text-small shadow-lift"><p className="mb-1 text-caption text-muted">{label}</p>{payload.map((item) => <p key={item.name} className="text-ink"><span className="mr-2 inline-block h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />{item.name}: <span className="font-semibold tabular-nums">{item.value ?? NO_DATA}</span></p>)}</div>;
}

export function EmptyNote({ children = "No records in this period." }) {
  return <p className="py-6 text-center text-small text-muted">{children}</p>;
}
